import { useEffect, useState } from "react";
import { ChevronDown, Download, Loader2, Settings2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ENTITY_FILTERS = [
  { v: "", l: "TUTTE" },
  { v: "contatto", l: "CONTATTI" },
  { v: "appuntamento", l: "APPUNTAMENTI" },
  { v: "attività", l: "ATTIVITÀ" },
  { v: "impostazioni", l: "IMPOSTAZIONI" },
  { v: "partner", l: "PARTNER" },
  { v: "registro", l: "REGISTRO" },
];

const PROTOCOL_OPTIONS = ["GENERAL", "AC_START", "AC_HUNT", "AC_SELECT", "AC_INVEST", "AC_MANAGEMENT"];

const ACTION_LABELS = {
  percorso_clienti: "Fase del percorso clienti",
  scheda_modificata: "Scheda cliente modificata",
  classificazione_demo: "Classificazione DEMO",
  esito_consulenza: "Esito / prossimo passo",
  attività_creata: "Attività creata",
  attività_completata: "Attività completata",
  attività_annullata: "Attività annullata",
  attività_riattivata: "Attività riattivata",
  attività_riprogrammata: "Attività riprogrammata",
  appuntamento_stato: "Stato appuntamento",
  appuntamento_riprogrammato: "Appuntamento riprogrammato",
  protocollo_consigliato: "Protocollo consigliato",
  contatto_archiviato: "Contatto archiviato",
  contatto_ripristinato: "Contatto ripristinato",
  contatto_ceestinato: "Contatto nel cestino",
  contatto_purgato: "Contatto eliminato definitivamente",
  impostazioni_reminder: "Impostazioni promemoria",
  impostazioni_registro: "Impostazioni registro",
  archiviazione_registro: "Voci trasferite in archivio",
  archiviazione_manuale: "Archiviazione manuale",
  archivio_pulito: "Archivio oltre conservazione rimosso",
  registro_esportato: "Registro esportato (CSV)",
  promemoria_inviato: "Promemoria email inviato",
  partner_stato: "Stato candidatura partner",
  partner_eliminato: "Candidatura partner eliminata",
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};

const inputCls = "w-full border border-[#c9a45c]/25 bg-[#0d0d0d] px-3 py-2 text-[12px] text-cream outline-none transition-colors focus:border-[#c9a45c]/60";
const labelCls = "block text-[9px] font-bold uppercase tracking-[0.14em] text-[#8f8f8f] mb-1";

// Registro modifiche: storico amministrativo in sola lettura con archivio verificato,
// filtri avanzati ed esportazione CSV riservata all'amministratore.
export default function AuditLogPanel() {
  const [entries, setEntries] = useState(null);
  const [source, setSource] = useState("operativo"); // operativo | archivio
  const [view, setView] = useState("operative"); // operative | tecnico | tutte
  const [filters, setFilters] = useState({ entity: "", date_from: "", date_to: "", actor: "", protocol: "", q: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [retention, setRetention] = useState(null);
  const [retentionDraft, setRetentionDraft] = useState(null);
  const [showRetention, setShowRetention] = useState(false);
  const [showBackup, setShowBackup] = useState(false);
  const [backupPass, setBackupPass] = useState("");
  const [backupPass2, setBackupPass2] = useState("");
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  const load = (f = filters, src = source, vw = view) => {
    setEntries(null);
    const params = { limit: 200, skip: 0 };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    if (vw !== "tutte") params.category = vw;
    api
      .get(src === "archivio" ? "/audit-archive" : "/audit-log", { params })
      .then(({ data }) => setEntries(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/settings/audit")
      .then(({ data }) => { setRetention(data); setRetentionDraft(data); })
      .catch(() => {});
  }, []);

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { source, limit: 5000 };
      if (view !== "tutte") params.category = view;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/audit-log/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `registro_AC_HUB_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Esportazione CSV avviata.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const saveRetention = async () => {
    try {
      const { data } = await api.patch("/settings/audit", retentionDraft);
      setRetention(data);
      toast.success("Politica di conservazione aggiornata.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadEncryptedBackup = async () => {
    setBackupBusy(true);
    setBackupResult(null);
    try {
      const body = backupPass ? { passphrase: backupPass } : {};
      const { data, headers } = await api.post("/maintenance/backup/encrypted", body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = (headers["content-disposition"] || "").match(/filename=([^;]+)/)?.[1] || `backup_AC_HUB_${new Date().toISOString().slice(0, 10)}.aechub`;
      a.click();
      window.URL.revokeObjectURL(url);
      setBackupResult({
        id: headers["x-backup-id"] || "",
        sha: headers["x-backup-sha256"] || "",
        generated: headers["x-generated-passphrase"] || "",
      });
      setBackupPass("");
      setBackupPass2("");
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { detail = null; }
      }
      toast.error(detail || "Verifica la passphrase: minimo 12 caratteri con lettere e numeri.");
    } finally {
      setBackupBusy(false);
    }
  };

  return (
    <div className="pb-16" data-testid="audit-panel">
      <div className="flex flex-wrap items-center gap-2" data-testid="audit-source">
        {[{ v: "operativo", l: "REGISTRO" }, { v: "archivio", l: "ARCHIVIO" }].map((s) => (
          <button
            key={s.v}
            onClick={() => { setSource(s.v); load(filters, s.v); }}
            className={`border px-4 py-2 text-[9px] font-bold tracking-[0.14em] transition-colors duration-200 ${
              source === s.v ? "border-gold bg-[#c9a45c]/12 text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
            }`}
            data-testid={`audit-source-${s.v}`}
          >
            {s.l}
          </button>
        ))}
        <span className="mx-1 hidden h-6 w-px bg-[#c9a45c]/15 sm:block" />
        {[{ v: "operative", l: "ATTIVITÀ OPERATIVE" }, { v: "tecnico", l: "STORICO TECNICO" }, { v: "tutte", l: "TUTTE" }].map((v) => (
          <button
            key={v.v}
            onClick={() => { setView(v.v); load(filters, source, v.v); }}
            className={`border px-3 py-2 text-[8px] font-bold tracking-[0.14em] transition-colors duration-200 ${
              view === v.v ? "border-gold bg-[#c9a45c]/12 text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
            }`}
            data-testid={`audit-view-${v.v}`}
          >
            {v.l}
          </button>
        ))}
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`inline-flex items-center gap-1.5 border px-3 py-2 text-[9px] font-bold tracking-[0.14em] transition-colors duration-200 ${
            showFilters ? "border-gold text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
          }`}
          data-testid="audit-filters-toggle"
        >
          <ChevronDown size={11} className={`transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`} /> FILTRI
        </button>
        <button
          onClick={exportCsv}
          disabled={exporting}
          className="inline-flex items-center gap-1.5 border border-[#c9a45c]/25 px-3 py-2 text-[9px] font-bold uppercase tracking-[0.14em] text-[#929292] transition-colors duration-200 hover:text-cream disabled:opacity-50"
          data-testid="audit-export-csv"
        >
          {exporting ? <Loader2 size={11} className="animate-spin text-gold" /> : <Download size={11} />} ESCI IN CSV
        </button>
        <button
          onClick={() => { setShowBackup(true); setBackupResult(null); }}
          className="inline-flex items-center gap-1.5 border border-gold/60 px-3 py-2 text-[9px] font-bold uppercase tracking-[0.14em] text-gold-light transition-colors duration-200 hover:bg-[#c9a45c]/12"
          data-testid="audit-backup-open"
        >
          <Settings2 size={11} /> BACKUP CIFRATO
        </button>
        <span className="ml-auto flex items-center gap-1.5 text-[9px] uppercase tracking-[0.12em] text-[#8f8f8f]">
          <ShieldCheck size={11} className="text-gold" /> Sola lettura — {source === "archivio" ? "storico archiviato" : "registro operativo"}
        </span>
      </div>

      {showBackup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="audit-backup-modal" onClick={(e) => { if (e.target === e.currentTarget) setShowBackup(false); }}>
          <div className="w-full max-w-md border border-[#c9a45c]/40 bg-[#101010] p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gold-light">Backup completo cifrato</p>
            <p className="mt-2 text-[11px] leading-relaxed text-[#9a9791]">
              Copia dell'intero database (schede, cronologie, richieste, prenotazioni, registro, archivio, visite, impostazioni) cifrata AES-256.
              Senza la passphrase il file è indecifrabile: conségnala in un luogo sicuro. Nessun invio automatico a servizi esterni.
            </p>
            {!backupResult && (
              <div className="mt-4 grid gap-3">
                <div>
                  <label className={labelCls}>Passphrase (min 12 caratteri, lettere e numeri)</label>
                  <input type="text" value={backupPass} onChange={(e) => setBackupPass(e.target.value)} placeholder="lascia vuoto per generarla" className={inputCls} data-testid="backup-passphrase" />
                </div>
                {backupPass && (
                  <div>
                    <label className={labelCls}>Conferma passphrase</label>
                    <input type="text" value={backupPass2} onChange={(e) => setBackupPass2(e.target.value)} className={inputCls} data-testid="backup-passphrase-confirm" />
                    {backupPass2 && backupPass !== backupPass2 && <p className="mt-1 text-[10px] text-[#e08484]">Le passphrase non coincidono.</p>}
                  </div>
                )}
                <button
                  onClick={downloadEncryptedBackup}
                  disabled={backupBusy || (backupPass !== "" && backupPass !== backupPass2)}
                  className="btn btn-gold flex items-center justify-center gap-2 px-4 py-3 text-[10px] font-bold tracking-[0.14em] disabled:opacity-50"
                  data-testid="backup-download"
                >
                  {backupBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  {backupPass ? "CIFRA E SCARICA" : "GENERA PASSPHRASE E SCARICA"}
                </button>
                <button onClick={() => setShowBackup(false)} className="border border-[#c9a45c]/25 px-4 py-2 text-[9px] font-bold uppercase tracking-[0.14em] text-[#929292] transition-colors hover:text-cream" data-testid="backup-cancel">
                  Annulla
                </button>
              </div>
            )}
            {backupResult && (
              <div className="mt-4 border border-[#c9a45c]/30 bg-[#0d0d0d] p-4" data-testid="backup-result">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gold-light">Backup creato</p>
                <p className="mt-2 font-mono text-[10px] text-[#9a9791]">ID: {backupResult.id}</p>
                <p className="font-mono text-[10px] text-[#9a9791]">Verifica SHA-256: {backupResult.sha.slice(0, 16)}…</p>
                {backupResult.generated && (
                  <div className="mt-3 border border-gold/50 bg-[#c9a45c]/10 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gold-light">Passphrase generata — consérvala ORA</p>
                    <p className="mt-1 break-all font-mono text-[12px] text-cream" data-testid="backup-generated-passphrase">{backupResult.generated}</p>
                    <p className="mt-2 text-[10px] text-[#e08484]">Non sarà mostrata di nuovo: senza di essa il file è indecifrabile.</p>
                  </div>
                )}
                <button onClick={() => setShowBackup(false)} className="mt-4 w-full border border-[#c9a45c]/25 px-4 py-2 text-[9px] font-bold uppercase tracking-[0.14em] text-[#929292] transition-colors hover:text-cream" data-testid="backup-close">
                  Ho conservato la passphrase — chiudi
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showFilters && (
        <div className="mt-3 border border-[#c9a45c]/18 bg-[#101010] p-4" data-testid="audit-filters-panel">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div>
              <label className={labelCls}>Dal</label>
              <input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className={inputCls} data-testid="audit-filter-date-from" />
            </div>
            <div>
              <label className={labelCls}>Al</label>
              <input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className={inputCls} data-testid="audit-filter-date-to" />
            </div>
            <div>
              <label className={labelCls}>Responsabile</label>
              <input type="text" placeholder="email responsabile" value={filters.actor} onChange={(e) => setF("actor", e.target.value)} className={inputCls} data-testid="audit-filter-actor" />
            </div>
            <div>
              <label className={labelCls}>Protocollo</label>
              <select value={filters.protocol} onChange={(e) => setF("protocol", e.target.value)} className={inputCls} data-testid="audit-filter-protocol">
                <option value="">Tutti</option>
                {PROTOCOL_OPTIONS.map((p) => <option key={p} value={p}>{p.replace("AC_", "AC ")}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Cliente / ricerca nel registro</label>
              <input type="text" placeholder="nome cliente, descrizione, testo…" value={filters.q} onChange={(e) => setF("q", e.target.value)} className={inputCls} data-testid="audit-filter-q" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => load()}
              className="btn btn-gold px-4 py-2 text-[9px] font-bold tracking-[0.14em]"
              data-testid="audit-filters-apply"
            >
              APPLICA FILTRI
            </button>
            <button
              onClick={() => { const clean = { entity: "", date_from: "", date_to: "", actor: "", protocol: "", q: "" }; setFilters(clean); load(clean); }}
              className="border border-[#c9a45c]/25 px-3 py-2 text-[9px] font-bold uppercase tracking-[0.14em] text-[#929292] transition-colors hover:text-cream"
              data-testid="audit-filters-reset"
            >
              Azzera
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="audit-entity-filters">
        {ENTITY_FILTERS.map((f) => (
          <button
            key={f.v}
            onClick={() => { const next = { ...filters, entity: f.v }; setFilters(next); load(next); }}
            className={`border px-3 py-2 text-[9px] font-bold tracking-[0.14em] transition-colors duration-200 ${
              filters.entity === f.v ? "border-gold bg-[#c9a45c]/12 text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
            }`}
            data-testid={`audit-filter-${f.v || "all"}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-2" data-testid="audit-list">
        {entries === null && (
          <div className="flex items-center gap-3 border border-[#c9a45c]/18 bg-[#101010] p-6 text-[13px] text-[#929292]">
            <Loader2 size={14} className="animate-spin text-gold" /> Caricamento {source === "archivio" ? "archivio" : "registro"}…
          </div>
        )}
        {entries !== null && entries.length === 0 && (
          <div className="border border-[#c9a45c]/18 bg-[#101010] p-8 text-center text-[13px] text-[#929292]" data-testid="audit-empty">
            {source === "archivio" ? "Nessuna voce in archivio con questi filtri." : "Nessuna operazione registrata con questi filtri."}
          </div>
        )}
        {(entries || []).map((e) => (
          <div key={e.id} className="border border-[#c9a45c]/18 bg-[#101010] px-4 py-3" data-testid={`audit-entry-${e.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <p className="text-[13px] text-[#dedbd4]">
                <span className="mr-2 text-[9px] font-bold uppercase tracking-[0.12em] text-gold-light">{ACTION_LABELS[e.action] || e.action}</span>
                {e.entity_label && <span className="text-cream">{e.entity_label}</span>}
              </p>
              <p className="font-mono text-[10px] tracking-[0.1em] text-[#8f8f8f]">{fmt(e.at)}</p>
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-[#9a9791]">
              {e.details || ""}
              {e.actor_email && e.actor_email !== "sistema" ? ` — responsabile: ${e.actor_email}` : e.actor_email === "sistema" ? " — sistema" : ""}
            </p>
            {(e.prev_state || e.new_state) && (
              <p className="mt-1 font-mono text-[10px] tracking-[0.08em] text-gold-light" data-testid={`audit-states-${e.id}`}>
                {e.prev_state || "—"} <span className="mx-1 text-[#8f8f8f]">→</span> {e.new_state || "—"}
              </p>
            )}
          </div>
        ))}
      </div>

      {source === "operativo" && retention && (
        <div className="mt-6 border border-[#c9a45c]/18 bg-[#101010]">
          <button
            onClick={() => setShowRetention((v) => !v)}
            className="flex w-full items-center gap-2 px-4 py-3 text-[9px] font-bold uppercase tracking-[0.14em] text-[#929292] transition-colors hover:text-cream"
            data-testid="audit-retention-toggle"
          >
            <Settings2 size={11} className="text-gold" /> POLITICA DI CONSERVAZIONE
            <ChevronDown size={11} className={`ml-auto transition-transform duration-200 ${showRetention ? "rotate-180" : ""}`} />
          </button>
          {showRetention && (
            <div className="border-t border-[#c9a45c]/12 p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Voci nel registro operativo</label>
                  <input type="number" min="500" max="20000" value={retentionDraft.operational_keep} onChange={(e) => setRetentionDraft({ ...retentionDraft, operational_keep: Number(e.target.value) })} className={inputCls} data-testid="audit-retention-keep" />
                  <p className="mt-1 text-[9px] leading-relaxed text-[#8f8f8f]">Le più recenti restano nel registro; le altre vanno in archivio (min 500, max 20000).</p>
                </div>
                <div>
                  <label className={labelCls}>Conservazione archivio (giorni)</label>
                  <input type="number" min="30" max="3650" value={retentionDraft.archive_retention_days} onChange={(e) => setRetentionDraft({ ...retentionDraft, archive_retention_days: Number(e.target.value) })} className={inputCls} data-testid="audit-retention-days" />
                  <p className="mt-1 text-[9px] leading-relaxed text-[#8f8f8f]">Dopo questo periodo le voci archiviate vengono rimosse (min 30, max 3650).</p>
                </div>
              </div>
              <button
                onClick={saveRetention}
                disabled={!retentionDraft || (retention && retentionDraft && (retentionDraft.operational_keep === retention.operational_keep && retentionDraft.archive_retention_days === retention.archive_retention_days))}
                className="btn btn-gold mt-3 px-4 py-2 text-[9px] font-bold tracking-[0.14em] disabled:opacity-40"
                data-testid="audit-retention-save"
              >
                SALVA POLITICA
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
